import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { applyAssistantDirectiveOnBlur } from "../../services/waveAssistant";
import { DraftTextField } from "./DraftTextField";
import { WaveMarkdownField } from "./WaveMarkdownField";
import { WaveMarkdownPreview } from "./WaveMarkdownPreview";
import { indexWaveDocument, pollWaveIndexStatus, getWaveIndexStatus, isWaveRagSingleChunkDegraded, waveRagIndexedBadgeLabel, waveRagIndexedBadgeTitle } from "../../services/waveRag";
import { openBlobDownload } from "../../services/waveBlobUpload";
import { showWaveNotification } from "../../services/notifications";
import { trackActivity } from "../../services/waveSession";
import { WaveAttachmentViewer } from "../editor/WaveAttachmentViewer";
import { WaveGalleryBlock } from "./WaveGalleryBlock";
import { WaveTodoListBlock } from "./WaveTodoListBlock";
import {
  WaveBlockContextMenu,
  type WaveBlockContextMenuItem,
} from "./WaveBlockContextMenu";
import {
  WAVE_APP_ID,
  WaveBlockKind,
  collapsedBlockPreviewLabel,
  driftDeepLinkForWave,
  formatAgentWidgetDisplayText,
  isAgentWidgetRelation,
  participantEmail,
  resolveAttachmentPlaceholders,
  type WaveBlock,
  type WaveCommentRecord,
  type WaveDocEditor,
  type WhaleMissionAttachment,
  type WaveParticipantRecord,
  type WavePresenceRecord,
  type WaveThreadView,
} from "@vida/wave-core";
import { getIdToken } from "../../auth";
import { getAdminApiBaseUrl } from "../../services/waveAccountAPI";
import {
  blockKindColor,
  blockKindGlyph,
  formatBlockMetadataLabel,
  relationGlyph,
  relationLabel,
} from "../editor/waveFeedTokens";
import {
  PresenceDots,
  remoteEditingBlockStyle,
} from "../presence/PresenceDots";
import { WaveBlockAgentButton } from "./WaveBlockAgentButton";

type BlockProps = {
  waveId: string;
  spaceId?: string;
  block: WaveBlock;
  editor?: WaveDocEditor | null;
  currentUserSub: string;
  participants?: WaveParticipantRecord[];
  mentionParticipants?: WaveParticipantRecord[];
  comments?: WaveCommentRecord[];
  remoteContentRevision?: number;
  readOnly?: boolean;
  remoteEditors?: WavePresenceRecord[];
  onBeginEditing?: () => void;
  onTypingActivity?: () => void;
  onEndEditing?: () => void;
  onSelectionChange?: (offset: number, endOffset: number) => void;
  onUpdate?: (block: WaveBlock) => void;
  onVote?: (choice: string) => void;
  onApprove?: () => void;
  onToggleTask?: () => void;
  onToggleBlips?: () => void;
  onPostTodoComment?: (anchor: string, body: string, parentId: string | null) => void;
  onPostGalleryComment?: (
    anchor: string,
    body: string,
    parentId: string | null,
    galleryItemId: string,
  ) => void;
  onToggleReaction?: (commentId: string, emoji: string) => void;
  onAfterTodoEdit?: () => void;
  onArchive?: (blockId: string) => void;
  onOpenAnalyst?: (block: WaveBlock) => void;
  onAskWhale?: (block: WaveBlock) => void;
  /** @deprecated Agent badge is shown in the feed gutter stack. */
  linkedAgentTask?: WaveBlock | null;
  threadView?: WaveThreadView;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  showsBlipThread?: boolean;
  blipCount?: number;
  /** Widget can be collapsed (nested: stub; top-level: soft preview). */
  collapsible?: boolean;
  /** `stub` = one-line stub; `soft` = clamped body preview (~5–6 lines). */
  collapseMode?: "stub" | "soft";
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  /** Active nested children count (top-level widgets only). */
  nestedChildCount?: number;
  /** Whether nested children are hidden from the feed. */
  nestedHidden?: boolean;
  onToggleNested?: () => void;
  /** All nested children of this top-level widget are in stub collapse. */
  allNestedCollapsed?: boolean;
  onToggleCollapseAllNested?: () => void;
};

type EditHandlers = {
  onFocus?: () => void;
  onBlur?: () => void;
  onTypingActivity?: () => void;
};

export const WaveBlockView = memo(function WaveBlockView({
  waveId,
  spaceId = "",
  block,
  editor = null,
  currentUserSub,
  participants = [],
  mentionParticipants,
  comments = [],
  remoteContentRevision = 0,
  readOnly,
  remoteEditors = [],
  onBeginEditing,
  onTypingActivity,
  onEndEditing,
  onSelectionChange,
  onUpdate,
  onVote,
  onApprove,
  onToggleTask,
  onToggleBlips,
  onPostTodoComment,
  onPostGalleryComment,
  onToggleReaction,
  onAfterTodoEdit,
  onArchive,
  onOpenAnalyst,
  onAskWhale,
  linkedAgentTask: _linkedAgentTask = null,
  threadView = "timeline",
  canMoveUp = false,
  canMoveDown = false,
  onMoveUp,
  onMoveDown,
  showsBlipThread,
  blipCount = 0,
  collapsible = false,
  collapseMode = "stub",
  collapsed = false,
  onToggleCollapse,
  nestedChildCount = 0,
  nestedHidden = false,
  onToggleNested,
  allNestedCollapsed = false,
  onToggleCollapseAllNested,
}: BlockProps) {
  const edit: EditHandlers = readOnly
    ? {}
    : {
        onFocus: onBeginEditing,
        onBlur: onEndEditing,
        onTypingActivity,
      };

  const [todoHideDone, setTodoHideDone] = useState(false);
  const [todoFocusItemId, setTodoFocusItemId] = useState<string | null>(null);
  const [fileBusy, setFileBusy] = useState(false);
  const galleryOpenPickerRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    setTodoHideDone(false);
    setTodoFocusItemId(null);
  }, [block.id]);

  useEffect(() => {
    if (block.kind !== WaveBlockKind.File || !editor) return;
    const s3Key = block.payload.s3Key?.trim();
    if (!s3Key) return;
    const ragStatus = block.payload.ragStatus;
    if (ragStatus !== "pending" && ragStatus !== "indexing" && ragStatus !== "none") return;

    let cancelled = false;
    void (async () => {
      try {
        const result = await pollWaveIndexStatus(waveId, s3Key, {
          onStatus: (status) => {
            if (cancelled) return;
            if (status === "indexing" && block.payload.ragStatus !== "indexing") {
              editor.setMetaString(block.id, "ragStatus", "indexing");
              onAfterTodoEdit?.();
            }
          },
        });
        if (cancelled) return;
        applyFileRagIndexMeta(editor, block.id, {
          indexed: result.indexed,
          chunks: result.chunks,
          reason: result.reason,
        });
        onAfterTodoEdit?.();
      } catch (e) {
        if (cancelled) return;
        const message = e instanceof Error ? e.message : "Indicizzazione non riuscita.";
        if (block.payload.ragStatus === "pending" || block.payload.ragStatus === "indexing") {
          editor.setMetaString(block.id, "ragStatus", "error");
          editor.setMetaString(block.id, "ragReason", message);
          onAfterTodoEdit?.();
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    block.id,
    block.kind,
    block.payload.ragStatus,
    block.payload.s3Key,
    editor,
    onAfterTodoEdit,
    waveId,
  ]);

  const handleFileDownload = useCallback(async () => {
    const p = block.payload;
    if (!p.s3Key) return;
    try {
      await openBlobDownload(p.s3Key, p.fileName, waveId);
    } catch {
      /* FileBlock shows inline errors on open */
    }
  }, [block.payload, waveId]);

  const handleFileIndex = useCallback(async () => {
    if (!editor || !block.payload.s3Key || !block.payload.fileName) return;
    setFileBusy(true);
    editor.setMetaString(block.id, "ragStatus", "indexing");
    editor.setMetaString(block.id, "ragReason", null);
    onAfterTodoEdit?.();
    try {
      const result = await indexWaveDocument(
        waveId,
        block.payload.s3Key,
        block.payload.fileName,
        block.payload.mimeType,
        { spaceId, blockId: block.id },
      );
      applyFileRagIndexMeta(editor, block.id, {
        indexed: result.indexed,
        chunks: result.chunks,
        reason: result.reason,
      });
      if (result.indexed) {
        trackActivity("Indicizzazione RAG", block.payload.fileName);
        showWaveNotification("Indicizzazione RAG", `${block.payload.fileName} indicizzato.`);
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : "Indicizzazione non riuscita.";
      editor.setMetaString(block.id, "ragStatus", "error");
      editor.setMetaString(block.id, "ragReason", message);
      trackActivity("Indicizzazione RAG", message);
      showWaveNotification("Indicizzazione RAG", message);
    } finally {
      setFileBusy(false);
      onAfterTodoEdit?.();
    }
  }, [block.id, block.payload, editor, onAfterTodoEdit, spaceId, waveId]);

  const contextMenuItems = useMemo((): WaveBlockContextMenuItem[] => {
    const items: WaveBlockContextMenuItem[] = [];
    const wikiRefEarly = block.payload.wikiRef;
    if (wikiRefEarly?.slug) {
      const harborUrl = `https://harbor.oceany.ai/?view=wiki&page=${encodeURIComponent(wikiRefEarly.slug)}&fs=1`;
      items.push({
        id: "open-harbor-wiki",
        label: "Apri in Harbor",
        onClick: () => {
          window.open(harborUrl, "_blank", "noopener,noreferrer");
        },
      });
    }

    if (readOnly || block.payload.archived) return items;

    if (block.kind === WaveBlockKind.TodoList && editor) {
      items.push({
        id: "todo-add",
        label: "Aggiungi todo",
        onClick: () => {
          edit.onTypingActivity?.();
          const created = editor.addTodoItem(block.id, currentUserSub);
          setTodoFocusItemId(created.id);
          onAfterTodoEdit?.();
        },
      });
      items.push({
        id: "todo-toggle-done",
        label: todoHideDone ? "Mostra completati" : "Comprimi completati",
        onClick: () => setTodoHideDone((v) => !v),
      });
      items.push({
        id: "todo-drift",
        label: "Apri in Drift",
        onClick: () => {
          window.open(driftDeepLinkForWave(waveId), "_blank", "noopener,noreferrer");
        },
      });
    }

    if (block.kind === WaveBlockKind.Gallery && editor) {
      const coalesced = block.id.startsWith("wave-img-group-");
      if (!coalesced) {
        items.push({
          id: "gallery-add",
          label: "Aggiungi immagine",
          onClick: () => galleryOpenPickerRef.current?.(),
        });
      }
    }

    if (block.kind === WaveBlockKind.File) {
      const p = block.payload;
      const uploadStatus = p.uploadStatus ?? "uploading";
      const ragStatus = p.ragStatus ?? "none";
      const canDownload = Boolean(p.s3Key && uploadStatus === "ready");
      const canIndex =
        Boolean(editor && uploadStatus === "ready" && p.s3Key && ragStatus !== "indexing");
      const indexLabel =
        ragStatus === "indexed" ? "Re-indicizza per @documenti" : "Indicizza per @documenti";

      if (canDownload) {
        items.push({
          id: "file-download",
          label: "Scarica",
          onClick: () => void handleFileDownload(),
        });
      }
      if (canIndex) {
        items.push({
          id: "file-index",
          label: fileBusy ? "Indicizzazione…" : indexLabel,
          disabled: fileBusy,
          onClick: () => void handleFileIndex(),
        });
      }
    }

    const isTopLevel = !block.payload.parentId;
    const showMove =
      threadView === "story" &&
      editor &&
      isTopLevel &&
      !block.id.startsWith("wave-img-group-") &&
      (onMoveUp || onMoveDown);
    if (showMove) {
      if (onMoveUp) {
        items.push({
          id: "move-up",
          label: "Sposta su",
          disabled: !canMoveUp,
          onClick: () => onMoveUp(),
        });
      }
      if (onMoveDown) {
        items.push({
          id: "move-down",
          label: "Sposta giù",
          disabled: !canMoveDown,
          onClick: () => onMoveDown(),
        });
      }
    }

    if (onArchive) {
      items.push({
        id: "archive",
        label: "Archivia",
        separatorBefore: items.length > 0,
        onClick: () => onArchive(block.id),
      });
    }

    return items;
  }, [
    block,
    currentUserSub,
    edit,
    editor,
    fileBusy,
    handleFileDownload,
    handleFileIndex,
    onAfterTodoEdit,
    onArchive,
    onOpenAnalyst,
    onMoveDown,
    onMoveUp,
    canMoveDown,
    canMoveUp,
    threadView,
    readOnly,
    todoHideDone,
    waveId,
  ]);

  const showContextMenu = contextMenuItems.length > 0;
  const relation = block.payload.relation;
  const isNestedChild = Boolean(block.payload.parentId);
  const isAgentRelation = relation === "agent_task" || relation === "agent_response";
  const isRunningAgentTask =
    relation === "agent_task" &&
    block.payload.agentStatus !== "done" &&
    block.payload.agentStatus !== "failed" &&
    block.payload.agentStatus !== "error";
  const showWhaleButton =
    Boolean(onAskWhale) &&
    !readOnly &&
    !isRunningAgentTask &&
    (isNestedChild || !isAgentRelation);
  const isStubMode = collapsible && collapseMode === "stub";
  const isSoftMode = collapsible && collapseMode === "soft";
  const showStub = isStubMode && collapsed && onToggleCollapse;
  const showTopCollapseBtn =
    collapsible &&
    onToggleCollapse &&
    (isStubMode || (isSoftMode && !collapsed));

  const bodyCtx = {
    waveId,
    spaceId,
    editor,
    currentUserSub,
    participants,
    mentionParticipants,
    comments,
    readOnly,
    remoteContentRevision,
    hasRemoteEditors: remoteEditors.length > 0,
    onUpdate,
    onVote,
    onApprove,
    onToggleTask,
    onPostTodoComment,
    onPostGalleryComment,
    onToggleReaction,
    onAfterTodoEdit,
    onSelectionChange,
    edit,
    todoHideDone,
    todoFocusItemId,
    onTodoFocusHandled: () => setTodoFocusItemId(null),
    galleryOpenPickerRef,
    fileBusy,
    collapsed: Boolean(collapsed && isSoftMode),
  };

  const wikiRef = block.payload.wikiRef;
  const blockMeta = (
    <div className="wave-block-meta">
      {wikiRef?.slug && (
        <span className="wave-block-wiki-ref" title="Esportato da HarborWiki">
          📖 HarborWiki · {wikiRef.title || wikiRef.slug}
        </span>
      )}
      {formatBlockMetadataLabel(block.createdAt, block.updatedAt) && (
        <span className="wave-block-dates">
          {formatBlockMetadataLabel(block.createdAt, block.updatedAt)}
        </span>
      )}
      {onToggleBlips && block.kind !== WaveBlockKind.TodoList && (
        <button
          type="button"
          className={`link-btn blip-toggle${blipCount > 0 ? " has-blips" : ""}`}
          onClick={onToggleBlips}
        >
          {showsBlipThread
            ? blipCount > 0
              ? `Commenti (${blipCount}) ▾`
              : "Commenti ▾"
            : blipCount > 0
              ? `Commenti (${blipCount})`
              : "Commenti"}
        </button>
      )}
    </div>
  );

  const showNestedToggle =
    !isNestedChild && nestedChildCount > 0 && Boolean(onToggleNested);
  const showNestedCollapseAll =
    !isNestedChild && nestedChildCount > 0 && Boolean(onToggleCollapseAllNested);

  return (
    <div
      className={`wave-block${remoteEditors.length ? " wave-block--remote-editing" : ""}${showContextMenu || showWhaleButton ? " wave-block--has-menu" : ""}${showStub ? " wave-block--collapsed" : ""}${isSoftMode && collapsed ? " wave-block--soft-collapsed" : ""}${collapsible ? " wave-block--collapsible" : ""}${isSoftMode ? " wave-block--soft-collapsible" : ""}${showNestedToggle ? " wave-block--has-nested-toggle" : ""}`}
      style={remoteEditingBlockStyle(remoteEditors, participants)}
    >
      {showWhaleButton && (
        <div className="wave-block-ai-btn-wrap">
          <WaveBlockAgentButton onClick={() => onAskWhale?.(block)} />
        </div>
      )}
      {showContextMenu && <WaveBlockContextMenu items={contextMenuItems} />}
      {remoteEditors.length > 0 && (
        <div className="wave-block-editing-badges">
          <PresenceDots editors={remoteEditors} participants={participants} />
        </div>
      )}
      {showStub ? (
        <button
          type="button"
          className="wave-block-collapsed-stub"
          aria-expanded={false}
          onClick={onToggleCollapse}
        >
          <span
            className="wave-block-collapsed-kind"
            style={{
              background: `color-mix(in srgb, ${blockKindColor(block.kind)} 18%, transparent)`,
              color: blockKindColor(block.kind),
            }}
            aria-hidden
          >
            {blockKindGlyph(block.kind)}
          </span>
          <span className="wave-block-collapsed-relation" title={relationLabel(relation)}>
            {relationGlyph(relation)}
          </span>
          <CollapsedBlockPreview block={block} />
          <span className="wave-block-collapsed-chevron" aria-hidden>
            ▸
          </span>
        </button>
      ) : isSoftMode ? (
        <>
          {showTopCollapseBtn && (
            <button
              type="button"
              className="wave-block-collapse-btn"
              aria-expanded
              aria-label="Comprimi widget"
              onClick={onToggleCollapse}
            >
              <span className="wave-block-collapse-chevron" aria-hidden>
                ▾
              </span>
            </button>
          )}
          <div
            className={`wave-block-soft-body${collapsed ? " wave-block-soft-body--clamped" : ""}`}
          >
            {renderBody(block, bodyCtx)}
            {blockMeta}
          </div>
          {collapsed && onToggleCollapse && (
            <button
              type="button"
              className="wave-block-soft-expand-bar"
              aria-expanded={false}
              aria-label="Espandi widget"
              onClick={onToggleCollapse}
            >
              <span className="wave-block-soft-expand-bar-label">Continua</span>
              <span className="wave-block-soft-expand-bar-chevron" aria-hidden>
                ▾
              </span>
            </button>
          )}
        </>
      ) : (
        <>
          {showTopCollapseBtn && (
            <button
              type="button"
              className="wave-block-collapse-btn"
              aria-expanded
              aria-label="Comprimi widget annidato"
              onClick={onToggleCollapse}
            >
              <span className="wave-block-collapse-chevron" aria-hidden>
                ▾
              </span>
            </button>
          )}
          {renderBody(block, bodyCtx)}
          {blockMeta}
        </>
      )}
      {showNestedToggle && (
        <div className="wave-block-nested-actions">
          {showNestedCollapseAll && (
            <button
              type="button"
              className="wave-block-nested-toggle wave-block-nested-collapse-all"
              aria-pressed={allNestedCollapsed}
              onClick={onToggleCollapseAllNested}
            >
              {allNestedCollapsed
                ? `Espandi ${nestedChildCount} widget interni`
                : `Comprimi ${nestedChildCount} widget interni`}
            </button>
          )}
          <button
            type="button"
            className={`wave-block-nested-toggle${nestedHidden ? " is-hidden" : ""}`}
            aria-pressed={nestedHidden}
            onClick={onToggleNested}
          >
            {nestedHidden
              ? `Mostra ${nestedChildCount} widget interni`
              : `Nascondi ${nestedChildCount} widget interni`}
          </button>
        </div>
      )}
    </div>
  );
}, waveBlockViewPropsEqual);

function presenceSignature(editors: WavePresenceRecord[]): string {
  return editors
    .map(
      (editor) =>
        `${editor.sub}:${editor.blockAnchorId ?? ""}:${editor.cursorOffset ?? ""}:${editor.cursorEndOffset ?? ""}:${editor.updatedAt}`,
    )
    .join("|");
}

function blockContentSignature(block: WaveBlock): string {
  return `${block.id}|${block.kind}|${block.sortIndex}|${JSON.stringify(block.payload)}`;
}

function waveBlockViewPropsEqual(prev: BlockProps, next: BlockProps): boolean {
  return (
    prev.waveId === next.waveId &&
    prev.spaceId === next.spaceId &&
    blockContentSignature(prev.block) === blockContentSignature(next.block) &&
    prev.editor === next.editor &&
    prev.currentUserSub === next.currentUserSub &&
    prev.participants === next.participants &&
    prev.mentionParticipants === next.mentionParticipants &&
    prev.comments === next.comments &&
    prev.remoteContentRevision === next.remoteContentRevision &&
    prev.readOnly === next.readOnly &&
    prev.showsBlipThread === next.showsBlipThread &&
    prev.blipCount === next.blipCount &&
    prev.collapsible === next.collapsible &&
    prev.collapseMode === next.collapseMode &&
    prev.collapsed === next.collapsed &&
    prev.onToggleCollapse === next.onToggleCollapse &&
    prev.nestedChildCount === next.nestedChildCount &&
    prev.nestedHidden === next.nestedHidden &&
    prev.onToggleNested === next.onToggleNested &&
    prev.allNestedCollapsed === next.allNestedCollapsed &&
    prev.onToggleCollapseAllNested === next.onToggleCollapseAllNested &&
    presenceSignature(prev.remoteEditors ?? []) ===
      presenceSignature(next.remoteEditors ?? []) &&
    prev.onUpdate === next.onUpdate &&
    prev.onVote === next.onVote &&
    prev.onApprove === next.onApprove &&
    prev.onToggleTask === next.onToggleTask &&
    prev.onBeginEditing === next.onBeginEditing &&
    prev.onTypingActivity === next.onTypingActivity &&
    prev.onEndEditing === next.onEndEditing &&
    prev.onSelectionChange === next.onSelectionChange &&
    prev.onToggleBlips === next.onToggleBlips &&
    prev.onPostTodoComment === next.onPostTodoComment &&
    prev.onToggleReaction === next.onToggleReaction &&
    prev.onAfterTodoEdit === next.onAfterTodoEdit &&
    prev.onArchive === next.onArchive
  );
}

const jobAttachmentsCache = new Map<string, WhaleMissionAttachment[]>();
const jobAttachmentsInflight = new Map<string, Promise<WhaleMissionAttachment[]>>();

async function fetchJobAttachments(jobId: string): Promise<WhaleMissionAttachment[]> {
  const cached = jobAttachmentsCache.get(jobId);
  if (cached) return cached;

  const inflight = jobAttachmentsInflight.get(jobId);
  if (inflight) return inflight;

  const promise = (async () => {
    const base = getAdminApiBaseUrl();
    if (!base) return [];

    try {
      const token = await getIdToken();
      const res = await fetch(
        `${base}/api/jobs/${encodeURIComponent(jobId)}/attachments`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            "X-Vida-AppId": WAVE_APP_ID,
          },
        },
      );
      if (!res.ok) return [];
      const body = (await res.json()) as { attachments?: WhaleMissionAttachment[] };
      const list = Array.isArray(body.attachments) ? body.attachments : [];
      jobAttachmentsCache.set(jobId, list);
      return list;
    } catch {
      return [];
    } finally {
      jobAttachmentsInflight.delete(jobId);
    }
  })();

  jobAttachmentsInflight.set(jobId, promise);
  return promise;
}

type AgentWidgetMarkdownProps = {
  waveId: string;
  block: WaveBlock;
  editor?: WaveDocEditor | null;
  remoteContentRevision?: number;
  hasRemoteEditors?: boolean;
  readOnly?: boolean;
  collapsed?: boolean;
  participants?: WaveParticipantRecord[];
  onUpdate?: (block: WaveBlock) => void;
  onSelectionChange?: (offset: number, endOffset: number) => void;
  edit: EditHandlers;
};

function AgentWidgetMarkdownField({
  waveId,
  block,
  editor,
  remoteContentRevision = 0,
  hasRemoteEditors = false,
  readOnly,
  collapsed = false,
  participants = [],
  onUpdate,
  onSelectionChange,
  edit,
}: AgentWidgetMarkdownProps) {
  const jobId = block.payload.agentJobId?.trim() ?? "";
  const [attachments, setAttachments] = useState<WhaleMissionAttachment[]>([]);
  const isRunningAgentTask =
    block.payload.relation === "agent_task" &&
    block.payload.agentStatus !== "done" &&
    block.payload.agentStatus !== "failed" &&
    block.payload.agentStatus !== "error";
  const effectiveReadOnly = Boolean(readOnly) || isRunningAgentTask;
  const agentQuestion = block.payload.agentQuestion?.trim() ?? "";
  const launchedBy = block.payload.launchedBy?.trim() ?? "";
  const questionAuthorEmail = launchedBy
    ? participantEmail(launchedBy, participants)
    : null;

  useEffect(() => {
    if (!jobId) {
      setAttachments([]);
      return;
    }

    if (
      block.payload.agentStatus === "done" ||
      block.payload.agentStatus === "failed"
    ) {
      jobAttachmentsCache.delete(jobId);
    }

    let cancelled = false;
    void fetchJobAttachments(jobId).then((list) => {
      if (!cancelled) setAttachments(list);
    });

    return () => {
      cancelled = true;
    };
  }, [jobId, block.payload.agentStatus, remoteContentRevision]);

  // TipTap (ramo editabile di WaveMarkdownField) non ha l'estensione Image e
  // scarta il markdown `![attachment](url)`: le immagini vanno renderizzate
  // fuori dal campo, in una striscia dedicata. Dal testo si tolgono le righe
  // immagine risolte per evitare paragrafi vuoti nell'editor.
  const imageAttachments = useMemo(
    () =>
      attachments.filter(
        (att) =>
          (att.mime?.startsWith("image/") || att.type === "image") &&
          /^https?:\/\//i.test(att.presignedUrl ?? ""),
      ),
    [attachments],
  );

  const displayOverride = useMemo(() => {
    if (!effectiveReadOnly) return undefined;
    const raw = block.payload.text ?? "";
    const base = isRunningAgentTask
      ? formatAgentWidgetDisplayText(raw) || raw
      : raw;
    if (!base) return base;
    const resolved = resolveAttachmentPlaceholders(base, attachments);
    if (imageAttachments.length === 0) return resolved;
    return resolved
      .split("\n")
      .filter((line) => !/^!\[[^\]]*\]\(https?:\/\/[^)]+\)\s*$/.test(line.trim()))
      .join("\n")
      .trim();
  }, [
    attachments,
    block.payload.text,
    effectiveReadOnly,
    imageAttachments,
    isRunningAgentTask,
  ]);

  const previewMarkdown = displayOverride ?? block.payload.text ?? "";
  const showCollapsedPreview = collapsed && Boolean(previewMarkdown.trim());

  return (
    <>
      {agentQuestion ? (
        <div className="wave-agent-question" title={agentQuestion}>
          <div className="wave-agent-question-text">
            <span className="wave-agent-question-mark" aria-hidden>
              ❝
            </span>{" "}
            {agentQuestion}
          </div>
          {questionAuthorEmail ? (
            <div className="wave-agent-question-author">{questionAuthorEmail}</div>
          ) : null}
        </div>
      ) : null}
      {showCollapsedPreview ? (
        <WaveMarkdownPreview markdown={previewMarkdown} clamp="soft" />
      ) : (
        <WaveMarkdownField
          waveId={waveId}
          block={block}
          editor={editor}
          remoteContentRevision={remoteContentRevision}
          hasRemoteEditors={hasRemoteEditors}
          readOnly={effectiveReadOnly}
          displayText={displayOverride}
          placeholder="Testo"
          onUpdate={onUpdate}
          onSelectionChange={onSelectionChange}
          edit={edit}
        />
      )}
      {imageAttachments.length > 0 && (
        <div className="wave-agent-attachment-strip">
          {imageAttachments.map((att) => (
            <img
              key={att.id}
              src={att.presignedUrl ?? undefined}
              alt="attachment"
              className="wave-attachment-image"
              loading="lazy"
            />
          ))}
        </div>
      )}
    </>
  );
}

function renderBody(
  block: WaveBlock,
  ctx: Omit<
    BlockProps,
    | "block"
    | "onToggleBlips"
    | "showsBlipThread"
    | "remoteEditors"
    | "onBeginEditing"
    | "onTypingActivity"
    | "onEndEditing"
  > & {
    edit: EditHandlers;
    hasRemoteEditors?: boolean;
    onSelectionChange?: (offset: number, endOffset: number) => void;
    todoHideDone?: boolean;
    todoFocusItemId?: string | null;
    onTodoFocusHandled?: () => void;
    galleryOpenPickerRef?: React.MutableRefObject<(() => void) | null>;
    fileBusy?: boolean;
    collapsed?: boolean;
  },
) {
  const {
    editor,
    readOnly,
    remoteContentRevision,
    hasRemoteEditors = false,
    onUpdate,
    onVote,
    onApprove,
    onToggleTask,
    currentUserSub,
    onSelectionChange,
    edit,
    waveId,
    spaceId = "",
    comments = [],
    participants = [],
    onPostTodoComment,
    onPostGalleryComment,
    onToggleReaction,
    onAfterTodoEdit,
    todoHideDone = false,
    todoFocusItemId = null,
    onTodoFocusHandled,
    galleryOpenPickerRef,
    fileBusy = false,
    collapsed = false,
  } = ctx;

  switch (block.kind) {
    case WaveBlockKind.Text: {
      const isAgentWidget = isAgentWidgetRelation(block.payload.relation);
      if (isAgentWidget) {
        return (
          <AgentWidgetMarkdownField
            waveId={waveId}
            block={block}
            editor={editor}
            remoteContentRevision={remoteContentRevision}
            hasRemoteEditors={hasRemoteEditors}
            readOnly={readOnly}
            collapsed={collapsed}
            participants={participants}
            onUpdate={onUpdate}
            onSelectionChange={onSelectionChange}
            edit={edit}
          />
        );
      }
      const textMarkdown = block.payload.text?.trim() ?? "";
      if (collapsed && textMarkdown) {
        return <WaveMarkdownPreview markdown={block.payload.text ?? ""} clamp="soft" />;
      }
      return (
        <WaveMarkdownField
          waveId={waveId}
          block={block}
          editor={editor}
          remoteContentRevision={remoteContentRevision}
          hasRemoteEditors={hasRemoteEditors}
          readOnly={readOnly}
          placeholder="Testo"
          onUpdate={onUpdate}
          onSelectionChange={onSelectionChange}
          edit={edit}
        />
      );
    }
    case WaveBlockKind.Heading:
      return (
        <DraftTextField
          waveId={waveId}
          block={block}
          field="text"
          editor={editor}
          remoteContentRevision={remoteContentRevision}
          hasRemoteEditors={hasRemoteEditors}
          readOnly={readOnly}
          className="wave-heading"
          placeholder="Titolo"
          onUpdate={onUpdate}
          onSelectionChange={onSelectionChange}
          edit={edit}
        />
      );
    case WaveBlockKind.Decision:
      return (
        <div>
          <DraftTextField
            waveId={waveId}
            block={block}
            field="title"
            editor={editor}
            remoteContentRevision={remoteContentRevision}
            hasRemoteEditors={hasRemoteEditors}
            readOnly={readOnly}
            className="wave-title"
            placeholder="Decisione"
            onUpdate={onUpdate}
            edit={edit}
          />
          {(block.payload.choices ?? []).map((choice) => {
            const voted = block.payload.votes?.[currentUserSub] === choice;
            return (
              <button
                key={choice}
                type="button"
                className={`choice-btn ${voted ? "selected" : ""}`}
                disabled={readOnly}
                onClick={() => {
                  edit.onTypingActivity?.();
                  if (editor) {
                    editor.setVote(block.id, currentUserSub, choice);
                  } else {
                    onVote?.(choice);
                  }
                }}
              >
                {voted ? "✓ " : "○ "}
                {choice}
              </button>
            );
          })}
        </div>
      );
    case WaveBlockKind.Signoff:
      return (
        <div>
          <div className="wave-title">{block.payload.title ?? "Sign-off"}</div>
          <button
            type="button"
            className="primary-btn"
            disabled={readOnly || block.payload.approvals?.[currentUserSub]}
            onClick={() => {
              edit.onTypingActivity?.();
              if (editor) {
                editor.setApproval(block.id, currentUserSub, true);
              } else {
                onApprove?.();
              }
            }}
          >
            {block.payload.approvals?.[currentUserSub]
              ? "Approvato ✓"
              : "Approva"}
          </button>
        </div>
      );
    case WaveBlockKind.Poll:
      return (
        <PollBlock
          waveId={waveId}
          block={block}
          editor={editor}
          readOnly={readOnly}
          currentUserSub={currentUserSub}
          remoteContentRevision={remoteContentRevision}
          onUpdate={onUpdate}
          onVote={onVote}
          edit={edit}
        />
      );
    case WaveBlockKind.Task:
      return (
        <div className="task-row">
          <button
            type="button"
            className="task-check"
            disabled={readOnly}
            onClick={() => {
              edit.onTypingActivity?.();
              if (editor) {
                editor.setTaskDone(block.id, !(block.payload.taskDone ?? false));
              } else {
                onToggleTask?.();
              }
            }}
          >
            {block.payload.taskDone ? "☑" : "☐"}
          </button>
          <DraftTextField
            waveId={waveId}
            block={block}
            field="title"
            editor={editor}
            remoteContentRevision={remoteContentRevision}
            hasRemoteEditors={hasRemoteEditors}
            readOnly={readOnly}
            multiline
            className="wave-title"
            placeholder="Descrizione task"
            onUpdate={onUpdate}
            edit={edit}
          />
        </div>
      );
    case WaveBlockKind.Gallery:
      return (
        <WaveGalleryBlock
          waveId={waveId}
          block={block}
          editor={editor}
          readOnly={readOnly}
          remoteContentRevision={remoteContentRevision}
          isCoalescedFeedGroup={block.id.startsWith("wave-img-group-")}
          hideAddButton
          registerOpenPicker={(open) => {
            if (galleryOpenPickerRef) galleryOpenPickerRef.current = open;
          }}
          comments={comments}
          participants={participants}
          mentionParticipants={ctx.mentionParticipants ?? ctx.participants ?? []}
          currentUserSub={currentUserSub}
          edit={edit}
          onAfterEdit={onAfterTodoEdit}
          onPostGalleryComment={
            onPostGalleryComment
              ? (body, parentId, galleryItemId) =>
                  onPostGalleryComment(block.id, body, parentId, galleryItemId)
              : undefined
          }
          onToggleReaction={onToggleReaction}
          onUploadError={(msg) => {
            if (typeof window !== "undefined") {
              console.warn("[wave gallery upload]", msg);
            }
          }}
        />
      );
    case WaveBlockKind.TodoList:
      return (
        <WaveTodoListBlock
          waveId={waveId}
          spaceId={spaceId}
          block={block}
          editor={editor}
          currentUserSub={currentUserSub}
          participants={ctx.participants ?? []}
          mentionParticipants={ctx.mentionParticipants ?? ctx.participants ?? []}
          comments={comments}
          readOnly={readOnly}
          remoteContentRevision={remoteContentRevision}
          hideHeaderMenu
          hideDone={todoHideDone}
          focusItemId={todoFocusItemId}
          onFocusHandled={onTodoFocusHandled}
          edit={edit}
          onPostComment={onPostTodoComment}
          onToggleReaction={onToggleReaction}
          onAfterEdit={onAfterTodoEdit}
        />
      );
    case WaveBlockKind.TidePageRef:
    case WaveBlockKind.DriftTodoRef:
    case WaveBlockKind.ReefArticleRef:
    case WaveBlockKind.PulseDocRef:
      return (
        <EmbedRefBlock
          block={block}
          editor={editor}
          readOnly={readOnly}
          onUpdate={onUpdate}
          edit={edit}
        />
      );
    case WaveBlockKind.File:
      return (
        <FileBlock
          waveId={waveId}
          block={block}
          editor={editor}
          readOnly={readOnly}
          hideMenu
          externalBusy={fileBusy}
          onAfterEdit={onAfterTodoEdit}
        />
      );
    default:
      return (
        <DraftTextField
          waveId={waveId}
          block={block}
          field="text"
          editor={editor}
          remoteContentRevision={remoteContentRevision}
          hasRemoteEditors={hasRemoteEditors}
          readOnly={readOnly}
          multiline
          className="wave-text"
          placeholder="Testo"
          onUpdate={onUpdate}
          onSelectionChange={onSelectionChange}
          edit={edit}
        />
      );
  }
}

function PollBlock({
  waveId,
  block,
  editor,
  readOnly,
  currentUserSub,
  remoteContentRevision,
  onUpdate,
  onVote,
  edit,
}: {
  waveId: string;
  block: WaveBlock;
  editor?: WaveDocEditor | null;
  readOnly?: boolean;
  currentUserSub: string;
  remoteContentRevision?: number;
  onUpdate?: (b: WaveBlock) => void;
  onVote?: (c: string) => void;
  edit: EditHandlers;
}) {
  const votes = block.payload.votes ?? {};
  const [draftTitle, setDraftTitle] = useState(block.payload.title ?? "");
  const [draftChoices, setDraftChoices] = useState(block.payload.choices ?? []);
  const editingTitleRef = useRef(false);
  const editingChoiceRef = useRef<number | null>(null);
  const seededDefaultsRef = useRef(false);

  useEffect(() => {
    if (!editingTitleRef.current) {
      setDraftTitle(block.payload.title ?? "");
    }
  }, [block.payload.title, remoteContentRevision]);

  useEffect(() => {
    if (editingChoiceRef.current === null) {
      setDraftChoices(block.payload.choices ?? []);
    }
  }, [block.payload.choices, remoteContentRevision]);

  useEffect(() => {
    seededDefaultsRef.current = false;
  }, [block.id]);

  useEffect(() => {
    if (readOnly || seededDefaultsRef.current) return;
    if ((block.payload.choices?.length ?? 0) > 0) return;
    const defaults = ["Opzione A", "Opzione B", "Opzione C"];
    if (editor) {
      editor.setChoices(block.id, defaults, block.payload.choices ?? []);
    } else if (onUpdate) {
      onUpdate({
        ...block,
        payload: { ...block.payload, choices: defaults },
      });
    } else {
      return;
    }
    seededDefaultsRef.current = true;
    setDraftChoices(defaults);
  }, [block.id, block.payload.choices, editor, onUpdate, readOnly]);

  const displayChoices =
    draftChoices.length > 0 ? draftChoices : (block.payload.choices ?? []);

  const commitTitle = (title: string) => {
    if (editor) {
      editor.setText(block.id, "title", title);
      return;
    }
    onUpdate?.({
      ...block,
      payload: { ...block.payload, title },
    });
  };

  const commitChoices = (next: string[], previousChoices?: string[]) => {
    if (editor) {
      editor.setChoices(block.id, next, previousChoices ?? block.payload.choices);
      return;
    }
    onUpdate?.({
      ...block,
      payload: { ...block.payload, choices: next },
    });
  };

  return (
    <div className="poll-block">
      <input
        className="wave-title poll-question"
        value={draftTitle}
        readOnly={readOnly}
        placeholder="Domanda del sondaggio"
        onFocus={edit.onFocus}
        onBlur={() => {
          edit.onBlur?.();
          editingTitleRef.current = false;
          void applyAssistantDirectiveOnBlur(waveId, draftTitle, (integrated) => {
            setDraftTitle(integrated);
            commitTitle(integrated);
          });
        }}
        onChange={(e) => {
          editingTitleRef.current = true;
          edit.onTypingActivity?.();
          setDraftTitle(e.target.value);
          commitTitle(e.target.value);
        }}
      />
      <div className="poll-options" role="list">
      {displayChoices.map((choice, index) => {
        const count = Object.values(votes).filter((v) => v === choice).length;
        const selected = votes[currentUserSub] === choice;
        return (
          <div key={`${choice}-${index}`} className="poll-row" role="listitem">
            <button
              type="button"
              className={`poll-vote-btn${selected ? " selected" : ""}`}
              disabled={readOnly}
              aria-label={selected ? `Voto per ${choice}` : `Vota ${choice}`}
              aria-pressed={selected}
              onClick={() => {
                edit.onTypingActivity?.();
                if (editor) {
                  editor.setVote(block.id, currentUserSub, choice);
                } else {
                  onVote?.(choice);
                }
              }}
            >
              {selected ? "●" : "○"}
            </button>
            <input
              className="wave-title poll-choice-input"
              value={choice}
              readOnly={readOnly}
              aria-label={`Opzione ${index + 1}`}
              onFocus={edit.onFocus}
              onBlur={edit.onBlur}
              onChange={(e) => {
                editingChoiceRef.current = index;
                edit.onTypingActivity?.();
                const old = displayChoices[index]!;
                const next = [...displayChoices];
                next[index] = e.target.value;
                setDraftChoices(next);
                commitChoices(next, displayChoices);
                if (old !== e.target.value && !editor) {
                  const nextVotes = { ...votes };
                  for (const [sub, val] of Object.entries(nextVotes)) {
                    if (val === old) nextVotes[sub] = e.target.value;
                  }
                  onUpdate?.({
                    ...block,
                    payload: { ...block.payload, choices: next, votes: nextVotes },
                  });
                }
                window.setTimeout(() => {
                  if (editingChoiceRef.current === index) {
                    editingChoiceRef.current = null;
                  }
                }, 100);
              }}
            />
            <span className="poll-count" aria-label={`${count} voti`}>
              {count}
            </span>
            {!readOnly && displayChoices.length > 1 && (
              <button
                type="button"
                className="poll-remove-btn"
                aria-label={`Rimuovi opzione ${choice}`}
                onClick={() => {
                  edit.onTypingActivity?.();
                  const next = displayChoices.filter((_, i) => i !== index);
                  setDraftChoices(next);
                  if (editor) {
                    editor.setChoices(block.id, next, displayChoices);
                  } else {
                    const nextVotes = Object.fromEntries(
                      Object.entries(votes).filter(([, v]) => v !== choice),
                    );
                    onUpdate?.({
                      ...block,
                      payload: {
                        ...block.payload,
                        choices: next,
                        votes: nextVotes,
                      },
                    });
                  }
                }}
              >
                −
              </button>
            )}
          </div>
        );
      })}
      </div>
      {!readOnly && (
        <button
          type="button"
          className="link-btn poll-add-btn"
          onClick={() => {
            edit.onTypingActivity?.();
            const next = [
              ...displayChoices,
              `Opzione ${displayChoices.length + 1}`,
            ];
            setDraftChoices(next);
            commitChoices(next);
          }}
        >
          + Aggiungi opzione
        </button>
      )}
    </div>
  );
}

function EmbedRefBlock({
  block,
  editor,
  readOnly,
  onUpdate,
  edit,
}: {
  block: WaveBlock;
  editor?: WaveDocEditor | null;
  readOnly?: boolean;
  onUpdate?: (b: WaveBlock) => void;
  edit: EditHandlers;
}) {
  const app = block.payload.refApp ?? "Vida";
  const [refTitle, setRefTitle] = useState(block.payload.refTitle ?? "");
  const [refId, setRefId] = useState(block.payload.refId ?? "");

  useEffect(() => {
    setRefTitle(block.payload.refTitle ?? "");
    setRefId(block.payload.refId ?? "");
  }, [block.payload.refTitle, block.payload.refId, block.id]);

  return (
    <div className="embed-ref">
      <span className="embed-app">{app}</span>
      <input
        className="wave-title"
        value={refTitle}
        readOnly={readOnly}
        placeholder="Titolo riferimento"
        onFocus={edit.onFocus}
        onBlur={edit.onBlur}
        onChange={(e) => {
          edit.onTypingActivity?.();
          setRefTitle(e.target.value);
          if (editor) {
            editor.setMetaString(block.id, "refTitle", e.target.value);
          } else {
            onUpdate?.({
              ...block,
              payload: { ...block.payload, refTitle: e.target.value },
            });
          }
        }}
      />
      <input
        className="wave-ref-id"
        value={refId}
        readOnly={readOnly}
        placeholder="ID risorsa"
        onFocus={edit.onFocus}
        onBlur={edit.onBlur}
        onChange={(e) => {
          edit.onTypingActivity?.();
          setRefId(e.target.value);
          if (editor) {
            editor.setMetaString(block.id, "refId", e.target.value);
          } else {
            onUpdate?.({
              ...block,
              payload: { ...block.payload, refId: e.target.value },
            });
          }
        }}
      />
    </div>
  );
}

function applyFileRagIndexMeta(
  editor: WaveDocEditor,
  blockId: string,
  result: { indexed: boolean; chunks?: number; reason?: string | null },
): void {
  editor.setMetaString(blockId, "ragStatus", result.indexed ? "indexed" : "error");
  if (!result.indexed) return;
  if (result.chunks != null && result.chunks > 0) {
    editor.setMetaInt(blockId, "ragChunks", result.chunks);
  }
  const reason = result.reason?.trim();
  editor.setMetaString(blockId, "ragReason", reason || null);
}

function collapsedStubMarkdownSource(block: WaveBlock): string | null {
  if (block.kind !== WaveBlockKind.Text) return null;
  if (isAgentWidgetRelation(block.payload.relation) && block.payload.agentQuestion?.trim()) {
    return null;
  }
  const text = block.payload.text?.trim();
  return text || null;
}

function CollapsedBlockPreview({ block }: { block: WaveBlock }) {
  const markdown = collapsedStubMarkdownSource(block);
  if (markdown) {
    return (
      <div className="wave-block-collapsed-preview wave-block-collapsed-preview--markdown">
        <WaveMarkdownPreview markdown={markdown} clamp="stub" />
      </div>
    );
  }
  return (
    <span className="wave-block-collapsed-preview">{collapsedBlockPreviewLabel(block)}</span>
  );
}

function fileIcon(mime?: string, name?: string): string {
  const m = (mime ?? "").toLowerCase();
  const n = (name ?? "").toLowerCase();
  if (m.startsWith("image/") || /\.(jpe?g|png|gif|webp)$/.test(n)) return "🖼️";
  if (m === "application/pdf" || n.endsWith(".pdf")) return "📄";
  if (m.includes("spreadsheet") || n.endsWith(".xlsx") || n.endsWith(".csv")) return "📊";
  if (m.includes("word") || n.endsWith(".docx")) return "📝";
  return "📎";
}

function formatBytes(n?: number): string {
  if (n == null || n <= 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function FileBlock({
  waveId,
  block,
  editor = null,
  onAfterEdit,
}: {
  waveId: string;
  block: WaveBlock;
  editor?: WaveDocEditor | null;
  readOnly?: boolean;
  hideMenu?: boolean;
  externalBusy?: boolean;
  onAfterEdit?: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const p = block.payload;
  const uploadStatus = p.uploadStatus ?? "uploading";
  const ragStatus = p.ragStatus ?? "none";
  const ragChunks = p.ragChunks;
  const ragDegraded = isWaveRagSingleChunkDegraded(ragChunks, p.fileSize, p.ragReason);

  useEffect(() => {
    if (!editor || block.kind !== WaveBlockKind.File) return;
    const s3Key = p.s3Key?.trim();
    if (!s3Key || ragStatus !== "indexed") return;
    if (ragChunks != null && ragChunks > 0) return;

    let cancelled = false;
    void (async () => {
      try {
        const status = await getWaveIndexStatus(waveId, s3Key);
        if (cancelled || status.ragStatus !== "indexed") return;
        applyFileRagIndexMeta(editor, block.id, {
          indexed: true,
          chunks: status.chunks,
          reason: status.reason,
        });
        onAfterEdit?.();
      } catch {
        /* best-effort fallback for legacy blocks without ragChunks meta */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    block.id,
    block.kind,
    editor,
    onAfterEdit,
    p.s3Key,
    ragChunks,
    ragStatus,
    waveId,
  ]);

  const handleOpen = () => {
    if (!p.s3Key || uploadStatus !== "ready") return;
    setError(null);
    setViewerOpen(true);
  };

  const canOpen = Boolean(p.s3Key && uploadStatus === "ready");

  return (
    <div className="wave-file-block">
      <button
        type="button"
        className={`wave-file-open${canOpen ? "" : " disabled"}`}
        disabled={!canOpen}
        onClick={handleOpen}
        aria-label={p.fileName ? `Apri ${p.fileName}` : "Apri allegato"}
      >
        <div className="wave-file-icon">{fileIcon(p.mimeType, p.fileName)}</div>
        <div className="wave-file-meta">
          <strong className="wave-file-name">{p.fileName || "Allegato"}</strong>
          <span className="wave-file-detail">
            {[p.mimeType, formatBytes(p.fileSize)].filter(Boolean).join(" · ")}
          </span>
          <div className="wave-file-badges">
            {uploadStatus === "uploading" && (
              <span className="wave-file-badge uploading">Caricamento…</span>
            )}
            {uploadStatus === "error" && (
              <span className="wave-file-badge error">Upload fallito</span>
            )}
            {uploadStatus === "ready" && ragStatus === "none" && (
              <span className="wave-file-badge ready">Pronto</span>
            )}
            {ragStatus === "pending" && (
              <span className="wave-file-badge indexing">In coda per indicizzazione…</span>
            )}
            {ragStatus === "indexing" && (
              <span className="wave-file-badge indexing">Indicizzazione…</span>
            )}
            {ragStatus === "indexed" && ragDegraded && (
              <span
                className="wave-file-badge degraded"
                title={
                  waveRagIndexedBadgeTitle(ragChunks, p.ragReason) ||
                  "Indicizzazione a blocco unico — usa Re-indicizza"
                }
              >
                Blocco unico — re-indicizza
              </span>
            )}
            {ragStatus === "indexed" && !ragDegraded && (
              <span
                className="wave-file-badge indexed"
                title={waveRagIndexedBadgeTitle(ragChunks, p.ragReason)}
              >
                {waveRagIndexedBadgeLabel(ragChunks)}
              </span>
            )}
            {ragStatus === "error" && (
              <span
                className="wave-file-badge error"
                title={p.ragReason?.trim() || undefined}
              >
                RAG errore
              </span>
            )}
          </div>
          {error && <p className="wave-file-error">{error}</p>}
        </div>
        {canOpen && <span className="wave-file-chevron" aria-hidden>›</span>}
      </button>

      {viewerOpen && p.s3Key && (
        <WaveAttachmentViewer
          waveId={waveId}
          s3Key={p.s3Key}
          fileName={p.fileName}
          mimeType={p.mimeType}
          onClose={() => setViewerOpen(false)}
        />
      )}
    </div>
  );
}
